import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Backdrop,
  CircularProgress,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  FilterList,
  Refresh,
  Add,
  Save,
  Cancel,
  AutoAwesome,
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  Code,
  Edit,
  Delete,
  Visibility as VisibilityIcon,
  ContentCopy,
} from '@mui/icons-material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { Logger } from 'utils/Logger';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { apiClient } from 'utils/ApiClient';
import { normalizeAiCreatedPrompt } from './aiPromptNormalize';
import { MarkdownPreview } from './MarkdownPreview';
import { getAnamnesisTileCenterColors } from './anamnesisTileTheme';

export interface AIPromptItem {
  _id: string;
  title: string;
  prompt: string;
  precision: number;
  aiCreatedPrompt: string;
  status?: 'processing' | 'ready' | 'failed';
  inQueue?: boolean;
  aiError?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

const VIEWMODE_STORAGE_KEY = 'anamnesis-ai-prompts-project-view-mode';
const AI_PROMPTS_LIST_PATH = '/anamnesis/ai-prompts';

type MarkdownFormat = 'bold' | 'italic' | 'strikethrough' | 'code';

const MARKDOWN_WRAP: Record<MarkdownFormat, { prefix: string; suffix: string }> = {
  bold: { prefix: '**', suffix: '**' },
  italic: { prefix: '*', suffix: '*' },
  strikethrough: { prefix: '~~', suffix: '~~' },
  code: { prefix: '`', suffix: '`' },
};

interface AIPromptProjectViewProps {
  projectName: string;
}

const renderInlineMarkdown = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|~~[^~]+~~|`[^`]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('~~')) {
      parts.push(<del key={key++}>{token.slice(2, -2)}</del>);
    } else if (token.startsWith('`')) {
      parts.push(
        <Box
          key={key++}
          component="code"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.85em',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            bgcolor: 'action.hover',
          }}
        >
          {token.slice(1, -1)}
        </Box>,
      );
    } else if (token.startsWith('*')) {
      parts.push(<em key={key++}>{token.slice(1, -1)}</em>);
    }
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

const MarkdownContent: React.FC<{ content: string }> = ({ content }) => {
  const blocks = content.split(/\n```/);
  const nodes: React.ReactNode[] = [];

  blocks.forEach((block, index) => {
    if (index === 0) {
      block.split('\n').forEach((line, lineIdx) => {
        nodes.push(
          <Typography key={`l-${index}-${lineIdx}`} variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 0.5 }}>
            {renderInlineMarkdown(line)}
          </Typography>,
        );
      });
      return;
    }

    const fenceEnd = block.indexOf('```');
    if (fenceEnd >= 0) {
      const codeBody = block.slice(block.indexOf('\n') + 1, fenceEnd);
      nodes.push(
        <Box
          key={`code-${index}`}
          component="pre"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.82rem',
            p: 1.5,
            my: 1,
            borderRadius: 1,
            bgcolor: 'action.hover',
            overflow: 'auto',
          }}
        >
          {codeBody}
        </Box>,
      );
      const remainder = block.slice(fenceEnd + 3);
      remainder.split('\n').forEach((line, lineIdx) => {
        if (!line.trim()) return;
        nodes.push(
          <Typography key={`r-${index}-${lineIdx}`} variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 0.5 }}>
            {renderInlineMarkdown(line)}
          </Typography>,
        );
      });
    }
  });

  return <Box>{nodes}</Box>;
};

export const AIPromptProjectView: React.FC<AIPromptProjectViewProps> = ({ projectName }) => {
  const theme = useTheme();
  const tileCenterColors = getAnamnesisTileCenterColors(theme);
  const encodedProject = encodeURIComponent(projectName);
  const projectPath = `${AI_PROMPTS_LIST_PATH}/${encodedProject}`;

  const [prompts, setPrompts] = useState<AIPromptItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
    const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
    return stored === 'table' ? 'table' : 'tile';
  });

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toolbarMenuOpen, setToolbarMenuOpen] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [promptTitle, setPromptTitle] = useState('');
  const [promptDescription, setPromptDescription] = useState('');
  const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingPrompt, setViewingPrompt] = useState<AIPromptItem | null>(null);

  const [aiValidateDialogOpen, setAiValidateDialogOpen] = useState(false);

  const showMessage = useCallback((message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = `/anamnesis/ai-prompts/${encodedProject}/prompts`;
      const data = await apiClient.get(endpoint);
      const rows = Array.isArray(data.data) ? data.data : [];
      setPrompts(
        rows.map((row: Record<string, unknown>) => ({
          _id: String(row._id ?? ''),
          title: String(row.title ?? ''),
          prompt: String(row.prompt ?? ''),
          precision: typeof row.precision === 'number' ? row.precision : 0,
          aiCreatedPrompt: normalizeAiCreatedPrompt(String(row.aiCreatedPrompt ?? '')),
          status: (row.status as AIPromptItem['status']) || 'ready',
          inQueue: row.inQueue === true,
          aiError: row.aiError ? String(row.aiError) : undefined,
          createdBy: row.createdBy ? String(row.createdBy) : undefined,
          updatedBy: row.updatedBy ? String(row.updatedBy) : undefined,
          createdAt: row.createdAt ? String(row.createdAt) : undefined,
          updatedAt: row.updatedAt ? String(row.updatedAt) : undefined,
        })),
      );
    } catch (error) {
      Logger.error('AIPromptProjectView: failed to fetch prompts', error);
      showMessage(`Failed to fetch prompts: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [encodedProject, showMessage]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts, refreshTrigger]);

  const filteredPrompts = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return prompts;
    return prompts.filter((item) => {
      const title = item.title?.toLowerCase() || '';
      const prompt = item.prompt?.toLowerCase() || '';
      return title.includes(q) || prompt.includes(q);
    });
  }, [prompts, searchTerm]);

  const resetDialog = () => {
    setPromptTitle('');
    setPromptDescription('');
    setSelection(null);
    setEditingPromptId(null);
  };

  const openNewPromptDialog = () => {
    resetDialog();
    setDialogOpen(true);
  };

  const openEditPromptDialog = (item: AIPromptItem) => {
    setEditingPromptId(item._id);
    setPromptTitle(item.title);
    setPromptDescription(item.prompt);
    setSelection(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    resetDialog();
  };

  const openViewDialog = (item: AIPromptItem) => {
    setViewingPrompt(item);
    setViewDialogOpen(true);
  };

  const closeViewDialog = () => {
    setViewDialogOpen(false);
    setViewingPrompt(null);
  };

  const applyMarkdownFormatting = (format: MarkdownFormat) => {
    const el = descriptionRef.current;
    const start = el ? el.selectionStart : (selection?.start ?? promptDescription.length);
    const end = el ? el.selectionEnd : (selection?.end ?? promptDescription.length);
    const hasSelection = start !== end;
    const before = promptDescription.substring(0, start);
    const selected = hasSelection ? promptDescription.substring(start, end) : 'text';
    const after = promptDescription.substring(end);
    const { prefix, suffix } = MARKDOWN_WRAP[format];
    const newText = before + prefix + selected + suffix + after;
    setPromptDescription(newText);
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        const cursor = start + prefix.length + selected.length + suffix.length;
        el.setSelectionRange(cursor, cursor);
      }
    });
  };

  const handleSavePrompt = async (validateWithAi = true) => {
    const title = promptTitle.trim();
    const prompt = promptDescription.trim();
    if (!title) {
      showMessage('Prompt Title is required', 'error');
      return;
    }
    if (!prompt) {
      showMessage('Prompt Description is required', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingPromptId) {
        await apiClient.put(`/anamnesis/ai-prompts/${encodedProject}/prompts/${editingPromptId}`, {
          title,
          prompt: promptDescription,
          validateWithAi,
        });
        showMessage(
          validateWithAi ? 'Prompt updated and queued for AI verification' : 'Prompt updated successfully',
          'success',
        );
      } else {
        await apiClient.post(`/anamnesis/ai-prompts/${encodedProject}/prompts`, {
          title,
          prompt: promptDescription,
        });
        showMessage('Prompt saved successfully', 'success');
      }
      closeDialog();
      setAiValidateDialogOpen(false);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      Logger.error('AIPromptProjectView: failed to save prompt', error);
      showMessage(`Failed to save prompt: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSaveClick = () => {
    const title = promptTitle.trim();
    const prompt = promptDescription.trim();
    if (!title) {
      showMessage('Prompt Title is required', 'error');
      return;
    }
    if (!prompt) {
      showMessage('Prompt Description is required', 'error');
      return;
    }
    setAiValidateDialogOpen(true);
  };

  const handleSaveClick = () => {
    if (editingPromptId) {
      handleEditSaveClick();
    } else {
      handleSavePrompt(true);
    }
  };

  const handleDeletePrompt = async (item: AIPromptItem) => {
    if (!window.confirm(`Delete prompt "${item.title}"?`)) return;
    try {
      await apiClient.delete(`/anamnesis/ai-prompts/${encodedProject}/prompts/${item._id}`);
      showMessage('Prompt deleted successfully', 'success');
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      Logger.error('AIPromptProjectView: failed to delete prompt', error);
      showMessage(`Failed to delete prompt: ${error instanceof Error ? error.message : String(error)}`, 'error');
    }
  };

  const truncatePrompt = (text: string, max = 120) => {
    const normalized = text.replace(/\s+/g, ' ').trim();
    if (normalized.length <= max) return normalized;
    return `${normalized.slice(0, max)}…`;
  };

  const handleCopyToClipboard = useCallback(async (text: string, label: string) => {
    const value = text.trim();
    if (!value) {
      showMessage(`Nothing to copy for ${label}`, 'error');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      showMessage(`${label} copied to clipboard`, 'success');
    } catch (error) {
      Logger.error('AIPromptProjectView: failed to copy to clipboard', error);
      showMessage(`Failed to copy ${label}`, 'error');
    }
  }, [showMessage]);

  const renderTilePrecisionLabel = (item: AIPromptItem) => {
    if (item.status === 'processing' && item.inQueue) {
      return (
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: tileCenterColors.foreground }}>
          Processing…
        </Typography>
      );
    }
    if (item.precision > 0) {
      return (
        <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: tileCenterColors.foreground, lineHeight: 1.2, textShadow: tileCenterColors.textShadow }}>
          {item.precision}%
        </Typography>
      );
    }
    return (
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: tileCenterColors.muted }}>
        —
      </Typography>
    );
  };

  const renderTileBadge = (item: AIPromptItem) => {
    if (item.precision <= 0) return null;
    return (
      <Chip
        size="small"
        color="success"
        label="AI-Verified"
        sx={{ fontSize: '0.72rem', fontWeight: 600 }}
      />
    );
  };

  const renderStatusChip = (item: AIPromptItem) => {
    if (item.status === 'processing' && item.inQueue) {
      return (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          icon={<CircularProgress size={12} color="inherit" />}
          label="processing…"
          sx={{ fontSize: '0.72rem' }}
        />
      );
    }
    if (item.status === 'failed') {
      return (
        <Chip
          size="small"
          color="error"
          variant="outlined"
          label="failed"
          sx={{ fontSize: '0.72rem' }}
        />
      );
    }
    if (item.precision > 0) {
      return (
        <Chip
          size="small"
          color={item.precision >= 70 ? 'success' : item.precision >= 40 ? 'warning' : 'default'}
          variant="outlined"
          label={`${item.precision}%`}
          sx={{ fontSize: '0.72rem' }}
        />
      );
    }
    return null;
  };

  const toolbarSurfaceSx = {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: { xs: 1, md: 2 },
    alignItems: 'center',
    mb: 3,
    p: 1.5,
    borderRadius: 2,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
  };

  const renderToolbarControls = (variant: 'desktop' | 'mobile-dialog' = 'desktop') => (
    <>
      <TextField
        label="Search Prompts"
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        fullWidth={variant === 'mobile-dialog'}
        sx={variant === 'desktop' ? { flexGrow: 1, minWidth: '200px' } : undefined}
      />
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={(_e, newMode) => {
          if (newMode !== null) {
            setViewMode(newMode);
            localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
          }
        }}
        size="small"
        sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}
      >
        <ToggleButton value="tile" aria-label="Tile view">
          <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
        </ToggleButton>
        <ToggleButton value="table" aria-label="Table view">
          <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
      <Tooltip title="Refresh">
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setRefreshTrigger((prev) => prev + 1)}
          size="small"
          sx={{ minWidth: 36, px: 1, flexShrink: 0 }}
          aria-label="Refresh"
        >
          <Refresh fontSize="small" />
        </Button>
      </Tooltip>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<Add />}
        onClick={openNewPromptDialog}
        size="small"
        sx={{ flexShrink: 0, fontWeight: 700 }}
      >
        NEW PROMPT
      </Button>
    </>
  );

  const renderTileActions = (item: AIPromptItem) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
      <Box display="flex" gap={0.5}>
        <Tooltip title="View">
          <Button
            color="secondary"
            variant="contained"
            size="small"
            onClick={(e) => { e.stopPropagation(); openViewDialog(item); }}
            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
          >
            <VisibilityIcon fontSize="small" />
          </Button>
        </Tooltip>
        <Tooltip title="Edit">
          <Button
            color="secondary"
            variant="contained"
            size="small"
            onClick={(e) => { e.stopPropagation(); openEditPromptDialog(item); }}
            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
          >
            <Edit fontSize="small" />
          </Button>
        </Tooltip>
      </Box>
      <Box display="flex" gap={0.5}>
        <Tooltip title="Delete">
          <Button
            color="error"
            variant="contained"
            size="small"
            onClick={(e) => { e.stopPropagation(); handleDeletePrompt(item); }}
            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
          >
            <Delete fontSize="small" />
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
      {loading && (
        <Backdrop sx={{ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <DynamicBreadcrumbs items={[
        { itemPath: '/dashboard', itemLabel: 'Home' },
        { itemPath: AI_PROMPTS_LIST_PATH, itemLabel: 'AI Prompts' },
        { itemPath: projectPath, itemLabel: projectName },
      ]} />

      <Box sx={toolbarSurfaceSx}>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          <IconButton
            color="secondary"
            onClick={() => setMobileMenuOpen(true)}
            size="large"
            sx={{ border: '1px solid', borderColor: 'secondary.main', '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' } }}
          >
            <FilterList />
          </IconButton>
        </Box>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 'auto' }}>
          <IconButton
            color="secondary"
            onClick={() => setToolbarMenuOpen(true)}
            size="large"
            sx={{ border: '1px solid', borderColor: 'secondary.main', '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' } }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexGrow: 1,
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <TextField
            label="Search Prompts"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1, minWidth: '220px', flexShrink: 1 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', flexShrink: 0 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_e, newMode) => {
                if (newMode !== null) {
                  setViewMode(newMode);
                  localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
                }
              }}
              size="small"
              sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}
            >
              <ToggleButton value="tile" aria-label="Tile view">
                <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
              </ToggleButton>
              <ToggleButton value="table" aria-label="Table view">
                <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Refresh">
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setRefreshTrigger((prev) => prev + 1)}
                size="small"
                sx={{ minWidth: 36, px: 1, flexShrink: 0 }}
                aria-label="Refresh"
              >
                <Refresh fontSize="small" />
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              onClick={openNewPromptDialog}
              size="small"
              sx={{ flexShrink: 0, fontWeight: 700 }}
            >
              NEW PROMPT
            </Button>
          </Box>
        </Box>
      </Box>

      <Dialog open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Search &amp; Filters</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {renderToolbarControls('mobile-dialog')}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMobileMenuOpen(false)} variant="outlined" color="secondary">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={toolbarMenuOpen} onClose={() => setToolbarMenuOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Actions</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              onClick={() => { openNewPromptDialog(); setToolbarMenuOpen(false); }}
              size="small"
              sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
            >
              NEW PROMPT
            </Button>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_e, newMode) => {
                if (newMode !== null) {
                  setViewMode(newMode);
                  localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
                }
              }}
              size="small"
              sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}
            >
              <ToggleButton value="tile" aria-label="Tile view">
                <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
              </ToggleButton>
              <ToggleButton value="table" aria-label="Table view">
                <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Refresh">
              <Button
                variant="contained"
                color="secondary"
                onClick={() => { setRefreshTrigger((prev) => prev + 1); setToolbarMenuOpen(false); }}
                size="small"
                sx={{ minWidth: 36, px: 1, alignSelf: 'flex-start' }}
                aria-label="Refresh"
              >
                <Refresh fontSize="small" />
              </Button>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToolbarMenuOpen(false)} variant="outlined" color="secondary">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{editingPromptId ? 'Edit Prompt' : 'New Prompt'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Prompt Title"
              value={promptTitle}
              onChange={(e) => setPromptTitle(e.target.value)}
              fullWidth
              size="small"
              required
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Prompt Description</Typography>
              <Box sx={{
                border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                borderRadius: '8px',
                overflow: 'hidden',
              }}>
                <Box sx={{
                  background: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f0f2f5',
                  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                  padding: '8px 12px',
                  display: 'flex',
                  gap: '4px',
                }}>
                  <ToggleButtonGroup size="small" sx={{
                    '& .MuiToggleButton-root': { border: 'none', color: theme.palette.mode === 'dark' ? theme.palette.grey[400] : '#54656f' },
                  }}>
                    <ToggleButton value="bold" onClick={() => applyMarkdownFormatting('bold')}>
                      <FormatBold fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="italic" onClick={() => applyMarkdownFormatting('italic')}>
                      <FormatItalic fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="strikethrough" onClick={() => applyMarkdownFormatting('strikethrough')}>
                      <FormatStrikethrough fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="code" onClick={() => applyMarkdownFormatting('code')}>
                      <Code fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <TextField
                  multiline
                  minRows={6}
                  maxRows={12}
                  value={promptDescription}
                  onChange={(e) => setPromptDescription(e.target.value)}
                  onSelect={(e) => {
                    const t = e.target as HTMLTextAreaElement;
                    setSelection({ start: t.selectionStart, end: t.selectionEnd });
                  }}
                  inputRef={descriptionRef}
                  variant="standard"
                  fullWidth
                  placeholder="Enter your prompt description (Markdown supported)"
                  InputProps={{ disableUnderline: true, style: { padding: '12px 16px', fontSize: '14px', lineHeight: '1.5' } }}
                  sx={{
                    '& .MuiInputBase-root': {
                      minHeight: '150px',
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.background.paper,
                    },
                    width: '100%',
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Markdown: **bold**, *italic*, ~~strikethrough~~, `code`
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} variant="outlined" color="secondary" startIcon={<Cancel />} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSaveClick} variant="contained" color="secondary" startIcon={<Save />} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={aiValidateDialogOpen} onClose={() => !saving && setAiValidateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Verify with AI?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Do you want to verify this updated prompt with AI? The OpenAI service will analyse it against the project knowledge graph and suggest an improved prompt with a precision rating.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => handleSavePrompt(false)}
            variant="outlined"
            color="secondary"
            disabled={saving}
          >
            No, save only
          </Button>
          <Button
            onClick={() => handleSavePrompt(true)}
            variant="contained"
            color="secondary"
            startIcon={<AutoAwesome />}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Yes, verify with AI'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewDialogOpen} onClose={closeViewDialog} fullWidth maxWidth="md">
        <DialogTitle>Prompt Details</DialogTitle>
        <DialogContent>
          {viewingPrompt && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{viewingPrompt.title}</Typography>
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" color="text.secondary">Original Prompt</Typography>
                  <Tooltip title="Copy to clipboard">
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={() => handleCopyToClipboard(viewingPrompt.prompt, 'Original Prompt')}
                      aria-label="Copy original prompt to clipboard"
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                  <MarkdownContent content={viewingPrompt.prompt} />
                </Box>
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" color="text.secondary">AI-Generated Prompt</Typography>
                  {viewingPrompt.aiCreatedPrompt &&
                    !(viewingPrompt.status === 'processing' && viewingPrompt.inQueue) &&
                    viewingPrompt.status !== 'failed' && (
                    <Tooltip title="Copy to clipboard">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() =>
                          handleCopyToClipboard(
                            normalizeAiCreatedPrompt(viewingPrompt.aiCreatedPrompt),
                            'AI-Generated Prompt',
                          )
                        }
                        aria-label="Copy AI-generated prompt to clipboard"
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {viewingPrompt.status === 'processing' && viewingPrompt.inQueue ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Still processing… Click Refresh to check for updates.</Typography>
                  </Box>
                ) : viewingPrompt.status === 'failed' ? (
                  <Alert severity="error" sx={{ mt: 0.5 }}>
                    {viewingPrompt.aiError || 'AI processing failed.'}
                  </Alert>
                ) : viewingPrompt.aiCreatedPrompt ? (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      maxHeight: 420,
                      overflow: 'auto',
                    }}
                  >
                    <MarkdownPreview content={normalizeAiCreatedPrompt(viewingPrompt.aiCreatedPrompt)} />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No AI-generated prompt yet.</Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Precision Rating</Typography>
                {viewingPrompt.status === 'processing' && viewingPrompt.inQueue ? (
                  <Chip size="small" color="warning" variant="outlined" label="processing…" />
                ) : viewingPrompt.precision > 0 ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{viewingPrompt.precision} / 100</Typography>
                      <Chip
                        size="small"
                        color={viewingPrompt.precision >= 70 ? 'success' : viewingPrompt.precision >= 40 ? 'warning' : 'default'}
                        variant="outlined"
                        label={`${viewingPrompt.precision}%`}
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={viewingPrompt.precision}
                      color={viewingPrompt.precision >= 70 ? 'success' : viewingPrompt.precision >= 40 ? 'warning' : 'inherit'}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">—</Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeViewDialog} variant="outlined" color="secondary">Close</Button>
        </DialogActions>
      </Dialog>

      {viewMode === 'tile' && (
        <Box display="flex" flexWrap="wrap" gap={2.5} sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}>
          {filteredPrompts.map((item) => (
            <Box
              key={item._id}
              onClick={() => openViewDialog(item)}
              sx={{
                width: 260,
                height: 220,
                borderRadius: 2.5,
                overflow: 'hidden',
                position: 'relative',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                flexShrink: 0,
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover .tile-overlay': { height: 150 },
                '&:hover .tile-details': { opacity: 1 },
                '&:hover .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                '@media (hover: none)': {
                  '& .tile-overlay': { height: 150 },
                  '& .tile-details': { opacity: 1 },
                  '& .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                },
              }}
            >
              <Box sx={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: theme.palette.dashboard?.counterHeroGradient ?? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 2,
              }}>
                <AutoAwesome sx={{ fontSize: 36, color: tileCenterColors.foreground, opacity: 0.85 }} />
                {renderTilePrecisionLabel(item)}
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  {renderTileBadge(item)}
                </Box>
              </Box>

              <Box
                className="tile-overlay"
                sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  overflow: 'hidden',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
                  transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                  height: 40, px: 1.5,
                  display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                }}
              >
                <Typography variant="subtitle2" noWrap className="tile-name-short" sx={{
                  color: '#fff', fontWeight: 700, fontSize: '0.82rem',
                  lineHeight: '40px', textShadow: '0 1px 3px rgba(0,0,0,0.4)', flexShrink: 0,
                  transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
                }}>
                  {item.title}
                </Typography>
                <Box className="tile-details" sx={{
                  opacity: 0, transition: 'opacity 0.3s ease 0.08s',
                  display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.25, pb: 1, flex: 1,
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem', lineHeight: 1.3, textShadow: '0 1px 3px rgba(0,0,0,0.4)', wordBreak: 'break-word' }}>
                    {item.title}
                  </Typography>
                  {item.updatedAt && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                      Updated: {new Date(item.updatedAt).toLocaleDateString()}
                    </Typography>
                  )}
                  {renderTileActions(item)}
                </Box>
              </Box>
            </Box>
          ))}
          {filteredPrompts.length === 0 && (
            <Box width="100%" display="flex" justifyContent="center" alignItems="center" py={6}>
              <Typography variant="body2" color="text.secondary">No prompts found.</Typography>
            </Box>
          )}
        </Box>
      )}

      {viewMode === 'table' && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.paper', p: 2 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Prompt Title</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Last Updated</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPrompts.map((item) => (
                  <TableRow key={item._id} hover>
                    <TableCell>{item.title}</TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      <Typography variant="body2" noWrap title={item.prompt}>
                        {truncatePrompt(item.prompt, 160)}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderStatusChip(item)}</TableCell>
                    <TableCell>
                      {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="View">
                          <IconButton
                            color="secondary"
                            size="small"
                            onClick={() => openViewDialog(item)}
                            sx={{ border: '1px solid', borderColor: 'secondary.main' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            color="secondary"
                            size="small"
                            onClick={() => openEditPromptDialog(item)}
                            sx={{ border: '1px solid', borderColor: 'secondary.main' }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeletePrompt(item)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredPrompts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No prompts found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AIPromptProjectView;
